import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

const PAGE_SIZE = 200

// Partner users are relatively few across all orgs, so we fetch them all once and
// group by organizationId client-side. This avoids an N+1 request-per-org-row pattern
// in the admin organizations table. Paginates until a short page to stay correct even
// if the server caps page size below the total count.
const fetchAllPartnerUsers = async (): Promise<UserService.User[]> => {
  const all: UserService.User[] = []
  let offset = 0
  for (;;) {
    const response = await apis.userService.listUsers({
      filter: { role: ['partner'] },
      limit: PAGE_SIZE,
      offset,
    })
    const page = response.data
    all.push(...page)
    if (page.length < PAGE_SIZE) break
    offset += page.length
  }
  return all
}

export const allPartnerUsersQueryKey = ['admin-all-partner-users'] as const

export const useAllPartnerUsers = () => {
  const query = useQuery({
    queryKey: allPartnerUsersQueryKey,
    queryFn: fetchAllPartnerUsers,
  })

  const partnersByOrgId = useMemo(() => {
    const map = new Map<string, UserService.User[]>()
    for (const user of query.data ?? []) {
      if (!user.organizationId) continue
      const list = map.get(user.organizationId)
      if (list) list.push(user)
      else map.set(user.organizationId, [user])
    }
    return map
  }, [query.data])

  return {
    partnersByOrgId,
    isLoading: query.isPending,
    isError: query.isError,
  }
}
